import { sankey, sankeyJustify, sankeyLinkHorizontal } from 'd3-sankey';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';

import {
  applyColumnFillLayout,
  EMPTY_LINK_ENDPOINTS,
  type SankeyValueScale,
  scaleLayoutValue,
} from './sankey-layout';
import { LABEL_PADDING } from './sankey-node-label';
import type { SankeyNodeRaw } from './sankey-node-tooltip';
import { validateLinks, validateNodes } from './sankey-validator';
import type {
  SankeyLink as Link,
  SankeyNode as Node,
  SankeyData,
  SankeyLinkValidated,
  SankeyNodeValidated,
  SankeyRenderLink,
} from './types';

const PLOT_TOP_PADDING = 18;
const PLOT_BOTTOM_PADDING = 8;
const NODE_WIDTH = 6;
const NODE_LINK_GAP = 2;
const LEFT_MARGIN = 100;
const RIGHT_MARGIN = 100;
const SIDE_LABEL_MAX_WIDTH = 115;
const SIDE_LABEL_MAX_WIDTH_SMALL = 30;
const SMALL_PLOT_WIDTH = 250;

const linkKeyFor = (link: SankeyLinkValidated): string =>
  `${link.source.id}__${link.target.id}`;

const isIntermediate = (node: SankeyNodeValidated): boolean =>
  (node.sourceLinks?.length ?? 0) > 0 && (node.targetLinks?.length ?? 0) > 0;

const resolveHubIsTarget = (
  link: SankeyLinkValidated,
  columnSize: (depth: number) => number
): boolean => {
  if (isIntermediate(link.target)) return true;
  if (isIntermediate(link.source)) return false;
  return columnSize(link.target.depth) <= columnSize(link.source.depth);
};

export type SankeyLayout = {
  isPlotSizeValid: boolean;
  maxDepth: number;
  nodesValidated: SankeyNodeValidated[];
  renderLinks: SankeyRenderLink[];
  rawByNode: Map<string, SankeyNodeRaw>;
  interiorLabelMaxWidth: number;
  sideLabelMaxWidth: number;
};

type UseSankeyLayoutArgs = {
  data: SankeyData | null;
  width: number;
  height: number;
  valueScale: SankeyValueScale;
  fillColumnHeight: boolean;
};

/** Runs the d3-sankey layout and derives everything the chart needs to render. */
export const useSankeyLayout = ({
  data,
  width,
  height,
  valueScale,
  fillColumnHeight,
}: UseSankeyLayoutArgs): SankeyLayout =>
  useMemo(() => {
    const hasData = !!data && data.nodes.length > 0 && data.links.length > 0;
    const hasViewport = width > 0 && height > 0;

    // Probe pass: only the column count is needed before the real layout.
    const maxDepth = (() => {
      if (!hasData || !hasViewport) return 0;
      const probe = sankey<Node, Link>()
        .nodeId((d) => d.id)
        .nodeAlign(sankeyJustify)
        .nodeWidth(NODE_WIDTH)
        .extent([
          [0, 0],
          [width, height],
        ]);
      const { nodes } = probe({
        nodes: cloneDeep(data.nodes),
        links: cloneDeep(data.links),
      });
      return nodes.reduce((max, node) => Math.max(max, node.depth ?? 0), 0);
    })();

    // At narrow widths the side labels collapse to SIDE_LABEL_MAX_WIDTH_SMALL,
    // so the full LEFT/RIGHT_MARGIN (sized for wide dashboards) would reserve
    // ~70px of dead space per side and starve the flow. Shrink the margins to
    // fit the small labels so the flow ribbons get room.
    const isSmallPlot = width < SMALL_PLOT_WIDTH;
    const leftMargin = isSmallPlot
      ? SIDE_LABEL_MAX_WIDTH_SMALL + LABEL_PADDING
      : LEFT_MARGIN;
    const rightMargin = isSmallPlot
      ? SIDE_LABEL_MAX_WIDTH_SMALL + LABEL_PADDING
      : RIGHT_MARGIN;
    const plotWidth = width - leftMargin - rightMargin;
    const plotHeight = height - PLOT_TOP_PADDING - PLOT_BOTTOM_PADDING;
    const isPlotSizeValid =
      hasData && hasViewport && plotWidth > 0 && plotHeight > 0;

    const numOfNodes = Math.max((data?.nodes.length ?? 1) - 1, 1);
    const nodePadding = Math.floor((height * 0.4) / numOfNodes);

    // Feed d3 the scaled magnitudes; keep each link's raw value for display.
    const scaledLinks = cloneDeep(data?.links ?? []).map((link) => ({
      ...link,
      rawValue: link.value,
      value: scaleLayoutValue(link.value, valueScale),
    }));

    const { nodes, links } = isPlotSizeValid
      ? sankey<Node, Link>()
          .nodeId((d) => d.id)
          .nodeAlign(sankeyJustify)
          // Order every column by total node value, largest at the top.
          .nodeSort((a, b) => (b.value ?? 0) - (a.value ?? 0))
          .nodeWidth(NODE_WIDTH)
          .nodePadding(nodePadding)
          .extent([
            [leftMargin, PLOT_TOP_PADDING],
            [leftMargin + plotWidth, PLOT_TOP_PADDING + plotHeight],
          ])({
          nodes: cloneDeep(data?.nodes ?? []),
          links: scaledLinks,
        })
      : { nodes: [], links: [] };

    const nodesValidated = validateNodes(nodes);
    const linksValidated = validateLinks(links);

    // Raw inflow/outflow totals per node id, for tooltips and node values.
    const rawByNode = new Map<string, SankeyNodeRaw>();
    const bump = (id: string, key: 'in' | 'out', amount: number) => {
      const entry = rawByNode.get(id) ?? { in: 0, out: 0 };
      entry[key] += amount;
      rawByNode.set(id, entry);
    };
    for (const link of linksValidated) {
      const raw = link.rawValue ?? link.value ?? 0;
      bump(link.source.id, 'out', raw);
      bump(link.target.id, 'in', raw);
    }

    const endpoints = fillColumnHeight
      ? applyColumnFillLayout({
          nodes: nodesValidated,
          top: PLOT_TOP_PADDING,
          plotHeight,
        })
      : EMPTY_LINK_ENDPOINTS;

    // A small horizontal gap keeps ribbons off the node bars.
    const sourcePoint = (link: SankeyLinkValidated): [number, number] => [
      link.source.x1 + NODE_LINK_GAP,
      (link.index !== undefined
        ? endpoints.sourceY.get(link.index)
        : undefined) ?? link.y0,
    ];
    const targetPoint = (link: SankeyLinkValidated): [number, number] => [
      link.target.x0 - NODE_LINK_GAP,
      (link.index !== undefined
        ? endpoints.targetY.get(link.index)
        : undefined) ?? link.y1,
    ];

    const linkPathGenerator = sankeyLinkHorizontal<Node, SankeyLinkValidated>()
      .source(sourcePoint)
      .target(targetPoint);

    const columnSize = (depth: number) => {
      let count = 0;
      for (const node of nodesValidated) if (node.depth === depth) count++;
      return count;
    };

    const renderLinks: SankeyRenderLink[] = linksValidated.flatMap(
      (link, i) => {
        const path = linkPathGenerator(link);
        if (path === null) return [];
        const rawValue = link.rawValue ?? link.value ?? 0;
        const sourceOutRaw = rawByNode.get(link.source.id)?.out ?? 0;
        const displayShare =
          link.share ??
          (sourceOutRaw > 0 ? rawValue / sourceOutRaw : undefined);
        const hubIsTarget = resolveHubIsTarget(link, columnSize);
        const [sx, sy] = sourcePoint(link);
        const [tx, ty] = targetPoint(link);
        return [
          {
            key: linkKeyFor(link),
            index: link.index ?? i,
            path,
            color: link.color,
            width: link.width,
            source: link.source,
            target: link.target,
            rawValue,
            displayShare,
            hubIsTarget,
            group: link.group,
            anchorX: (sx + tx) / 2,
            anchorY: (sy + ty) / 2,
          },
        ];
      }
    );

    const interiorLabelMaxWidth = Math.max(
      40,
      plotWidth / (maxDepth + 1) - LABEL_PADDING
    );

    const sideLabelMaxWidth = Math.min(
      width < SMALL_PLOT_WIDTH
        ? SIDE_LABEL_MAX_WIDTH_SMALL
        : SIDE_LABEL_MAX_WIDTH,
      leftMargin - LABEL_PADDING
    );

    return {
      isPlotSizeValid,
      maxDepth,
      nodesValidated,
      renderLinks,
      rawByNode,
      interiorLabelMaxWidth,
      sideLabelMaxWidth,
    };
  }, [data, width, height, valueScale, fillColumnHeight]);
